/**
 * positions.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for all certificate field positions.
 * All values are in INCHES (CSS `in` unit), measured from the physical
 * TOP-LEFT edge of the paper.
 *
 * Calibration: adjust values in 0.05in increments after a test print.
 * If ALL fields shift consistently → adjust PRINTER_MARGIN_* instead.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Printer hardware margin compensation ──────────────────────────────────────
export const PRINTER_MARGIN_TOP = 0; // inches — increase if text prints too HIGH
export const PRINTER_MARGIN_LEFT = 0; // inches — increase if text prints too LEFT

// ── Font ──────────────────────────────────────────────────────────────────────
export const CERTIFICATE_FONT = {
  //family: "'Kalam', 'Caveat', cursive",
  size: "14px",
  weight: "400",
  color: "#000000",
};

// ── Paper sizes ───────────────────────────────────────────────────────────────
// Bonafide uses a custom smaller form, Leaving uses standard A4.
export const PAPER = {
  bonafide: {
    width: "7.40in",
    height: "6.73in",
  },
  leaving: {
    width: "8.27in", // A4
    height: "11.69in",
  },
};

// ═════════════════════════════════════════════════════════════════════════════
// BONAFIDE CERTIFICATE
// ─────────────────────────────────────────────────────────────────────────────
// srNo is PRE-PRINTED on the form in Printed Format mode.
// In Plain Paper mode it is printed (handled in BonafideCertificate.jsx).
// ═════════════════════════════════════════════════════════════════════════════
export const BONAFIDE_POSITIONS = {
  certificateDate: { top: "1.70in", left: "6.00in" },
  studentName: { top: "2.62in", left: "2.55in" },
  className: { top: "3.30in", left: "3.66in" },
  sessionStart: { top: "4.00in", left: "0.96in" },
  sessionEnd: { top: "4.00in", left: "1.51in" },
  dob: { top: "3.95in", left: "6.06in" },
  dobInWords: { top: "4.60in", left: "1.41in" },
};

// ═════════════════════════════════════════════════════════════════════════════
// LEAVING CERTIFICATE (TC)
// ─────────────────────────────────────────────────────────────────────────────
// enrollmentNo / aadharNo: each digit printed individually in pre-printed boxes.
//   digitBoxWidth = physical width of one box on the form.
//
// dob / dateOfIssue: 8 digit boxes (DD MM YYYY, no slashes) — Option A.
//
// serialNo: skipped in Printed Format (pre-printed), printed in Plain Paper.
// remarks:  two lines — line1 and line2.
// ═════════════════════════════════════════════════════════════════════════════
export const LEAVING_POSITIONS = {
  // Digit-box fields (each character gets its own box)
  enrollmentNo: { top: "2.42in", left: "2.12in", digitBoxWidth: "0.26in" },
  aadharNo: { top: "2.85in", left: "2.12in", digitBoxWidth: "0.26in" },

  // Text fields
  studentName: { top: "3.30in", left: "2.25in" },
  fatherName: { top: "3.65in", left: "2.25in" },
  motherName: { top: "4.00in", left: "2.25in" },
  nationality: { top: "4.40in", left: "1.75in" },
  motherTongue: { top: "4.40in", left: "5.0in" },
  religion: { top: "4.75in", left: "1.75in" },
  caste: { top: "4.75in", left: "3.75in" },
  subCaste: { top: "4.75in", left: "6.34in" },
  placeOfBirth: { top: "5.15in", left: "1.75in" },
  district: { top: "5.15in", left: "4.25in" },
  state: { top: "5.15in", left: "6.12in" },

  // DOB — 8 digit boxes (no slashes), left = first box position
  dateOfBirth: { top: "5.65in", left: "2.40in", digitBoxWidth: "0.26in" },

  dateOfBirthInWords: { top: "5.90in", left: "2.50in" },
  lastSchool: { top: "6.25in", left: "2.50in" },
  progress: { top: "6.70in", left: "2.0in" },
  conduct: { top: "6.70in", left: "5.63in" },
  dateOfAdmission: { top: "7.10in", left: "3.75in" },
  stdInStudying: { top: "7.45in", left: "2.75in" },
  sinceWhen: { top: "7.45in", left: "6.63in" },
  dateOfLeaving: { top: "7.85in", left: "2.75in" },
  reasonOfLeaving: { top: "8.20in", left: "2.50in" },

  // Remarks — two lines
  remarks: {
    line1: { top: "8.60in", left: "1.53in" },
    line2: { top: "9.04in", left: "1.53in" },
  },

  // Date of Issue — 8 digit boxes (no slashes)
  dateOfIssue: { top: "9.40in", left: "1.60in", digitBoxWidth: "0.245in" },
};
